/* eslint-disable max-lines */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

import Input from '@components/atoms/ui-kit/input.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import Modal from '@components/atoms/modals/modal.tsx';

import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import {
  UpdateUserData,
  useUpdateUser
} from '@utils/api/users/use-update-user.tsx';
import { useGetUser } from '@utils/api/users/use-get-user.tsx';
import { USER_ROLES, UserRole } from '@utils/types/users/roles.ts';
import { BaseError } from '@utils/types/shared/base.ts';

type Props = {
  updateUserModalOpen: boolean;
  setUpdateUserModalOpen: (value: boolean) => void;
  selectedUser: UpdateUserData;
};

const FORM_ID = 'update-user-form';

const UpdateUserForm = ({
  updateUserModalOpen,
  setUpdateUserModalOpen,
  selectedUser
}: Props) => {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const isDark = theme === 'dark';

  const { data, isLoading } = useGetUser(
    selectedUser.id ? Number(selectedUser.id) : 0
  );
  const { mutateAsync: updateUser } = useUpdateUser();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialValues, setInitialValues] = useState<UpdateUserData | null>(
    null
  );

  const schema = useMemo(
    () =>
      yup.object().shape({
        first_name: yup
          .string()
          .trim()
          .required(t('first-name-required'))
          .max(50, t('first-name-too-long')),
        last_name: yup
          .string()
          .trim()
          .required(t('last-name-required'))
          .max(50, t('last-name-too-long')),
        email: yup
          .string()
          .trim()
          .lowercase()
          .email(t('invalid-email'))
          .required(t('email-required'))
          .max(100, t('email-too-long')),
        roles: yup
          .array()
          .of(yup.string().oneOf(USER_ROLES).defined())
          .min(1, t('role-required'))
          .default([])
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm<UpdateUserData>({
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      roles: []
    },
    mode: 'onBlur'
  });

  const isFormUnchanged = useCallback((): boolean => {
    if (!initialValues) return true;
    const currentValues = watch();
    return (
      currentValues.first_name === initialValues.first_name &&
      currentValues.last_name === initialValues.last_name &&
      currentValues.email === initialValues.email &&
      JSON.stringify([...currentValues.roles].sort()) ===
        JSON.stringify([...initialValues.roles].sort())
    );
  }, [initialValues, watch]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setUpdateUserModalOpen(false);
    }
  }, [isSubmitting, setUpdateUserModalOpen]);

  const onSubmit = useCallback(
    async (formData: UpdateUserData) => {
      if (!selectedUser.id) return;

      const { first_name, last_name, roles } = formData;
      const uniqueRoles = Array.from(new Set(roles));

      setIsSubmitting(true);
      const toastId = toast.loading(t('updating-user'));

      try {
        await updateUser({
          userId: selectedUser.id.toString(),
          userData: { first_name, last_name, roles: uniqueRoles }
        });
        toast.success(t('user-updated-successfully'), { id: toastId });
        setUpdateUserModalOpen(false);
      } catch (error) {
        const fallbackMessage = t('error-updating-user');
        const data = (error as AxiosError<BaseError>)?.response?.data;

        let messages: string[] = [];

        if (Array.isArray(data?.detail)) {
          type ValidationErr = { loc?: string[]; msg?: string };
          messages = (data.detail as ValidationErr[]).map(err => {
            const field = err.loc?.[1] ?? 'field';
            return `${field}: ${err.msg}`;
          });
        } else if (data?.message) {
          messages = [data.message];
        } else if (data?.error_description) {
          messages = [data.error_description];
        } else {
          messages = [fallbackMessage];
        }

        toast.error(messages.join('\n'), { id: toastId });
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedUser.id, updateUser, t, setUpdateUserModalOpen]
  );

  useEffect(() => {
    if (updateUserModalOpen && data?.result) {
      const filteredRoles = data.result.roles.filter((role): role is UserRole =>
        USER_ROLES.includes(role as UserRole)
      );

      const values = {
        first_name: data.result.first_name,
        last_name: data.result.last_name,
        email: data.result.email,
        roles: filteredRoles
      };

      reset(values);
      setInitialValues(values);
      setIsSubmitting(false);
    }
  }, [updateUserModalOpen, data?.result?.id, reset]);

  const textColor = useMemo(() => getColour('normalText'), [getColour]);
  const helperTextColor = useMemo(() => getColour('inactiveText'), [getColour]);
  const requiredColor = isDark ? 'rgb(248,113,113)' : 'rgb(220,38,38)';
  const errorTextColor = requiredColor;

  if (isLoading) return null;

  return (
    <Modal
      isOpen={updateUserModalOpen}
      onClose={handleClose}
      size="small"
      title={`${data?.result.first_name} ${data?.result.last_name}`}
      caption={t('update-user-details')}
      footerActions={[
        <Button
          key="cancel"
          type="button"
          onClick={handleClose}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={isSubmitting}
        />,
        <Button
          key="update"
          type="submit"
          form={FORM_ID}
          label={isSubmitting ? t('updating') : t('update')}
          size="small"
          variant="primary"
          disabled={isSubmitting || isFormUnchanged()}
        />
      ]}
    >
      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)} noValidate>
        <button type="submit" className="hidden" aria-hidden="true" />

        <div className="flex flex-col gap-5 w-full px-2">
          {/* Name Fields Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="first_name"
              control={control}
              render={({ field }) => (
                <Input
                  id="firstName"
                  label={t('first-name')}
                  placeholder={t('enter-first-name')}
                  type="text"
                  autoComplete="given-name"
                  required
                  {...field}
                  wrapperClass="w-full"
                  error={errors.first_name?.message}
                />
              )}
            />
            <Controller
              name="last_name"
              control={control}
              render={({ field }) => (
                <Input
                  id="lastName"
                  label={t('last-name')}
                  placeholder={t('enter-last-name')}
                  type="text"
                  autoComplete="family-name"
                  required
                  {...field}
                  wrapperClass="w-full"
                  error={errors.last_name?.message}
                />
              )}
            />
          </div>

          {/* Email Field */}
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Input
                id="email"
                label={t('email')}
                placeholder={t('enter-email-address')}
                type="email"
                autoComplete="email"
                required
                disabled
                {...field}
                wrapperClass="w-full"
                helperText={t('email-cannot-be-changed')}
                error={errors.email?.message}
              />
            )}
          />

          {/* Roles Section */}
          <fieldset className="w-full">
            <legend
              id="roles-label"
              className="block text-sm font-semibold mb-3"
              style={{ color: textColor }}
            >
              {t('role')}
              <span
                className="ml-1"
                style={{ color: requiredColor }}
                aria-label="required"
              >
                *
              </span>
            </legend>

            <Controller
              name="roles"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div
                  role="group"
                  aria-labelledby="roles-label"
                  aria-required="true"
                  aria-invalid={!!errors.roles}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                  {USER_ROLES.map(role => {
                    const id = `role-${role}`;
                    const isChecked = value.includes(role as UserRole);
                    return (
                      <Checkbox
                        key={role}
                        id={id}
                        name="roles"
                        label={t(role)}
                        checked={isChecked}
                        variant="primary"
                        disabled={isSubmitting}
                        onChange={e => {
                          const next = e.target.checked
                            ? [...value, role as UserRole]
                            : value.filter(r => r !== role);
                          onChange(next);
                        }}
                      />
                    );
                  })}
                </div>
              )}
            />

            {errors.roles?.message && (
              <p
                className="mt-2 text-sm"
                style={{ color: errorTextColor }}
                role="alert"
              >
                {errors.roles.message}
              </p>
            )}

            {!errors.roles?.message && (
              <p className="mt-2 text-xs" style={{ color: helperTextColor }}>
                {t('select-at-least-one-role')}
              </p>
            )}
          </fieldset>
        </div>
      </form>
    </Modal>
  );
};

export default UpdateUserForm;
